/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Label, MultiColumnList } from '@folio/stripes-components';
import { useIntl } from 'react-intl';
import { chain } from 'lodash';
import { useFieldArray } from 'react-final-form-arrays';
import HelpeModal from '../HelpeModal';
import { FieldsListColumnMapping } from '../../constants';

function FieldsList({ contentData, parentFields, schema }) {
  const [selectAll, setSelectAll] = useState(false);
  const { meta, fields } = useFieldArray('sectionFields');
  const intl = useIntl();

  const fieldsInParent = chain(parentFields.value)
    .map('sectionFields')
    .flatten()
    .filter({ schema })
    .value().length;

  useEffect(() => {
    if (fields.value) {
      const selectedList = fields?.value?.filter(
        (field) => field.schema === schema,
      );
      if (contentData.length === selectedList.length) {
        setSelectAll(true);
      }
    }
  }, [contentData, fields.value, schema]);

  const addBulkData = () => {
    contentData.forEach((field) => {
      fields.push(field);
    });
  };

  const updateSelection = (e) => {
    const value = e.target.value;
    if (e.target.checked) {
      const selectedIndex = contentData.findIndex((s) => s.name === value);
      const { id, categoryId, metadata, ...rest } = contentData[selectedIndex];
      fields.push(rest);
    } else {
      setSelectAll(false);
      const removedIndex = fields.value.findIndex(
        (fieldId) => fieldId.name === value,
      );
      fields.remove(removedIndex);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectAll(true);
      addBulkData();
    } else {
      setSelectAll(false);
      const indexes = [];
      contentData.forEach((field) => {
        const removedIndex = fields.value.findIndex(
          (fieldId) => fieldId.name === field.name,
        );
        indexes.push(removedIndex);
      });
      fields.removeBatch(indexes);
    }
  };

  const infoModalFormatter = (field) => {
    return (
      <HelpeModal
        fieldData={field}
        title={
          field.label.find((locale) => locale.locale === intl.locale)?.value
        }
        iconSize="medium"
      />
    );
  };

  const selectedFieldFormatter = (field) => {
    const fieldInParentFields = chain(parentFields.value)
      .map('sectionFields')
      .flatten()
      .filter({ name: field.name })
      .value().length;

    const fieldInSectionFields = chain(meta.initial)
      .flatten()
      .filter({ name: field.name })
      .value().length;

    const selected = !!(
      fields.value &&
      fields.value.findIndex((s) => s.name === field.name) !== -1
    );

    return (
      <Checkbox
        value={field.name}
        onChange={updateSelection}
        checked={selectAll || selected}
        onMouseDown={(e) => e.preventDefault()}
        disabled={fieldInParentFields && !fieldInSectionFields}
      />
    );
  };

  const labelFormatter = (field) => {
    return (
      <Label>
        {field.label.find((locale) => locale.locale === intl.locale)?.value}
      </Label>
    );
  };

  return (
    <MultiColumnList
      id={`${schema}-fields-list`}
      interactive={false}
      contentData={contentData}
      visibleColumns={[
        'index',
        'selected',
        'label',
        'isRequired',
        'canEdit',
        'isRepeatable',
        'info',
      ]}
      columnWidths={{
        index: '5%',
        selected: '5%',
        label: '45%',
        isRequired: '13%',
        canEdit: '13%',
        isRepeatable: '13%',
        info: '6%',
      }}
      columnMapping={{
        index: '#',
        selected: (
          <Checkbox
            value="selectAll"
            onChange={toggleSelectAll}
            checked={selectAll}
            disabled={
              !selectAll && fieldsInParent && parentFields.value?.length > 1
            }
            onMouseDown={(e) => e.preventDefault()}
          />
        ),
        ...FieldsListColumnMapping,
      }}
      formatter={{
        index: (field) => (field.rowIndex + 1).toString(),
        selected: (field) => selectedFieldFormatter(field),
        label: (field) => labelFormatter(field),
        isRequired: (field) => field.isRequired?.toString(),
        canEdit: (field) => field.canEdit?.toString(),
        isRepeatable: (field) => field.isRepeatable?.toString(),
        info: (field) => infoModalFormatter(field),
      }}
    />
  );
}

FieldsList.propTypes = {
  contentData: PropTypes.arrayOf(PropTypes.object),
  schema: PropTypes.string,
  parentFields: PropTypes.object,
};

export default FieldsList;
